import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import type { Task, TaskReferenceKind } from "../../lib/types";

/**
 * Task mutations (Sprint 26.0) — create / update / complete / archive /
 * restore / delete against the one task noun. Each invalidates both the
 * per-agent task lists (`agent-tasks`) and the workspace-wide roll-up
 * (`my-tasks`) plus the brain snapshot so every surface stays in sync,
 * including the top-chrome badge.
 */
function invalidateTasks(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["agent-tasks"] });
  qc.invalidateQueries({ queryKey: ["my-tasks"] });
  qc.invalidateQueries({ queryKey: ["agent-brain-snapshot"] });
}

export interface CreateTaskParams {
  /** Owning agent node id. */
  node_id: string;
  title: string;
  body?: string;
  instructions?: string;
  status?: "inbox" | "queued";
  parent_task_id?: string;
  due_by?: string;
  references?: Array<{ target_kind: TaskReferenceKind; target_id: string }>;
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ node_id, ...rest }: CreateTaskParams): Promise<{ task: Task; warning: string | null }> =>
      mcpClient.createTask({ agent_id: node_id, ...rest }),
    onSuccess: () => invalidateTasks(qc),
  });
}

export interface UpdateTaskParams {
  node_id: string;
  task_id: string;
  title?: string;
  body?: string;
  instructions?: string;
  status?: Task["status"];
  /** ISO date, or empty string to clear. */
  due_by?: string;
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ node_id, task_id, ...fields }: UpdateTaskParams): Promise<Task> =>
      mcpClient.updateTask(node_id, task_id, fields),
    onSuccess: () => invalidateTasks(qc),
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ node_id, task_id, summary }: { node_id: string; task_id: string; summary?: string }): Promise<Task> =>
      mcpClient.completeTask(node_id, task_id, summary),
    onSuccess: () => invalidateTasks(qc),
  });
}

export function useArchiveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ node_id, task_id }: { node_id: string; task_id: string }): Promise<Task> =>
      mcpClient.archiveTask(node_id, task_id),
    onSuccess: () => invalidateTasks(qc),
  });
}

export function useRestoreTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ node_id, task_id }: { node_id: string; task_id: string }): Promise<Task> =>
      mcpClient.restoreTask(node_id, task_id),
    onSuccess: () => invalidateTasks(qc),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ node_id, task_id }: { node_id: string; task_id: string }): Promise<void> => {
      await mcpClient.deleteTask(node_id, task_id);
    },
    onSuccess: () => invalidateTasks(qc),
  });
}
